import {
  createContext,
  forwardRef,
  useContext,
  useMemo,
  type CSSProperties,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  type HTMLAttributes,
} from 'react';

/**
 * Styling primitives for hand-rolled tables.
 *
 * `ResponsiveTable` is still the right answer for a sortable, mobile-aware data
 * grid and should be preferred. But ~107 screens build a plain `<table>` — a
 * totals row, a two-column summary, a nested detail grid — where react-table
 * column definitions would be more machinery than the job needs. Those tables
 * had accumulated ~193 distinct `<th>` recipes, the most common of which
 * (`text-xs font-medium text-gray-500`) is barely legible.
 *
 * These render the SAME treatment ResponsiveTable does, so a hand-rolled table
 * is visually indistinguishable from a real one, and the recipe changes in one
 * place rather than two.
 *
 * All of them pass native table attributes straight through, so `colSpan`,
 * `rowSpan`, `scope`, `title`, `onClick` and `style` work as normal.
 *
 * ── Why the typography sits on the SECTIONS, not the cells ──
 *
 * Tailwind resolves two conflicting utilities by their order in the generated
 * stylesheet, not by their order in the class attribute. A `TD` that emitted
 * `text-gray-900` would therefore fight a caller's `text-red-600` on the same
 * element and win or lose unpredictably — silently killing the conditional
 * colouring that the financial and status tables depend on.
 *
 * Size, weight, case and colour are declared once, on `Table` and `THead`, and
 * INHERITED by the cells. A cell that sets its own colour or weight always
 * wins, because a declaration on the element beats an inherited value outright
 * whatever order the stylesheet is in.
 *
 * Only what cannot be inherited stays on the cell: padding, alignment, borders.
 */

/** Inherited by every cell. A cell setting its own colour or size overrides it. */
const TABLE_BASE = 'text-base text-gray-900';

/** Inherited by header cells, overriding `TABLE_BASE`. */
const HEAD_BASE = 'bg-gray-50 text-sm font-semibold text-gray-800 uppercase tracking-wider';

/** Borders are not inherited, so the header rule lives on the cell. */
const HEAD_CELL_RULE = 'border-b-2 border-gray-200';

/**
 * Row density.
 *
 * `default` is ResponsiveTable's own padding and the one to reach for.
 * `compact` exists because the padding in this app's hand-rolled tables
 * clusters in two places, not one: `px-4 py-3` on the roomy ones and `px-3
 * py-2` or tighter on the wide ones. A fifteen-column financial table forced
 * into `px-4` scrolls sideways on a laptop, which would have made these
 * primitives unusable for exactly the tables that most need them.
 *
 * The typography is identical in both. Density changes how much room a row
 * takes, never how a table looks. Two values, both defined here; do not
 * introduce a third at a call site.
 */
export type TableDensity = 'default' | 'compact';

const padding: Record<TableDensity, string> = {
  default: 'px-4 py-3',
  compact: 'px-3 py-2',
};

/** The full `<th>` treatment at default density, for the rare header cell that
 *  has to be built by hand — one spanning two header rows, say — so that it
 *  still matches. Assumes it sits inside a `THead`. */
export const TABLE_HEAD_CELL_CLASS = `${padding.default} ${HEAD_CELL_RULE} text-left`;

/** The full `<td>` treatment at default density. Assumes a `Table` ancestor. */
export const TABLE_CELL_CLASS = `${padding.default} text-left`;

/** What `THead` puts on the `<thead>`. */
export const TABLE_HEAD_CLASS = HEAD_BASE;

type Align = 'left' | 'center' | 'right';

const alignClass: Record<Align, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/** Table-wide state, so it does not have to be repeated on every cell. */
const TableContext = createContext<{ sticky: boolean; density: TableDensity }>({
  sticky: false,
  density: 'default',
});

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  children: ReactNode;
  /** Pin the header row while the body scrolls. Needs a scrolling ancestor. */
  stickyHeader?: boolean;
  /** Any CSS length. Turns on vertical scrolling in this table's container. */
  maxHeight?: string;
  /** Row height. See `TableDensity`. */
  density?: TableDensity;
  /**
   * Render the scroll container. Pass `false` when the caller already has its
   * own `overflow-*` wrapper — nesting two scroll containers means the inner
   * one takes every gesture and the outer one never moves.
   */
  scroll?: boolean;
  /** Classes for the scroll container, not the `<table>`. */
  containerClassName?: string;
}

/**
 * The scroll container plus the `<table>`. Horizontal overflow scrolls inside
 * the container rather than the page, and uses the same quiet `rt-scroll`
 * scrollbar ResponsiveTable uses (index.css).
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  {
    children,
    stickyHeader = false,
    maxHeight,
    density = 'default',
    scroll = true,
    className = '',
    containerClassName = '',
    ...rest
  },
  ref
) {
  const style: CSSProperties | undefined = maxHeight ? { maxHeight } : undefined;
  const context = useMemo(() => ({ sticky: stickyHeader, density }), [stickyHeader, density]);

  // `w-full` is the default, but only as a default. A wide table needs
  // `min-w-full` so it can overflow its container and scroll; `w-full` pins it
  // to the container and squeezes the columns instead. Both resolve to 100%, so
  // whichever Tailwind emitted last would win — hence one or the other.
  const width = /\b(w-|min-w-|max-w-)/.test(className) ? '' : 'w-full';

  const table = (
    <table ref={ref} className={`${width} ${TABLE_BASE} ${className}`} {...rest}>
      {children}
    </table>
  );

  return (
    <TableContext.Provider value={context}>
      {scroll ? (
        <div
          className={`rt-scroll overflow-x-auto ${maxHeight ? 'overflow-y-auto' : ''} ${containerClassName}`}
          style={style}
        >
          {table}
        </div>
      ) : (
        table
      )}
    </TableContext.Provider>
  );
});

export interface THeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export function THead({ children, className = '', ...rest }: THeadProps) {
  const { sticky } = useContext(TableContext);
  return (
    <thead
      // z-20 keeps a pinned header above anything floating over the rows.
      className={`${HEAD_BASE} ${sticky ? 'sticky top-0 z-20' : ''} ${className}`}
      {...rest}
    >
      {children}
    </thead>
  );
}

export interface TBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
  /** Hairlines between rows. Matches ResponsiveTable. Off for layout tables. */
  divided?: boolean;
}

export function TBody({ children, className = '', divided = true, ...rest }: TBodyProps) {
  return (
    <tbody className={`${divided ? 'divide-y divide-gray-100' : ''} ${className}`} {...rest}>
      {children}
    </tbody>
  );
}

export interface TFootProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

/** Totals row. Reads as a summary rather than as another data row. */
export function TFoot({ children, className = '', ...rest }: TFootProps) {
  return (
    <tfoot className={`bg-gray-50 font-semibold border-t-2 border-gray-200 ${className}`} {...rest}>
      {children}
    </tfoot>
  );
}

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
  /** Row responds to a click. Adds the pointer and the stronger hover tint. */
  clickable?: boolean;
  /** Suppress the hover tint on a row that is not a data row. */
  hover?: boolean;
}

export function TR({ children, className = '', clickable = false, hover = true, ...rest }: TRProps) {
  const interaction = clickable
    ? 'hover:bg-blue-50/50 cursor-pointer active:bg-blue-50/80'
    : hover
      ? 'hover:bg-gray-50/80'
      : '';
  return (
    <tr className={`transition-colors duration-100 ${interaction} ${className}`} {...rest}>
      {children}
    </tr>
  );
}

export interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  /** Numbers right, everything else left. */
  align?: Align;
}

export function TH({ children, className = '', align = 'left', scope, ...rest }: THProps) {
  const { density } = useContext(TableContext);
  return (
    <th
      // A header cell with no `scope` is not announced as a header. The default
      // covers the overwhelmingly common case of a single header row.
      scope={scope ?? 'col'}
      className={`${padding[density]} ${HEAD_CELL_RULE} ${alignClass[align]} ${className}`}
      {...rest}
    >
      {children}
    </th>
  );
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
  align?: Align;
}

export function TD({ children, className = '', align = 'left', ...rest }: TDProps) {
  const { density } = useContext(TableContext);
  return (
    <td className={`${padding[density]} ${alignClass[align]} ${className}`} {...rest}>
      {children}
    </td>
  );
}
